import { api } from "../../lib/api";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Car, ArrowUUpLeft, Clock, IdentificationBadge, CaretRight, CaretDown, ArrowClockwise, Warning, CalendarBlank, Trophy, Wrench, Lock } from "@phosphor-icons/react";
import { Panel, EmptyState, count as fmt, dt, pct1 } from "../lux";

const TONE = { red: "text-red-400", amber: "text-amber-400", blue: "text-sky-400", green: "text-emerald-400", neutral: "text-muted-foreground" };
const TAG_TONE = { "Late": "text-red-400 border-red-400/40", "Awaiting approval": "text-amber-400 border-amber-400/40", "Out": "text-sky-400 border-sky-400/40", "Internal hold": "text-muted-foreground border-border" };

function lateText(mins) {
  const n = Math.max(0, Math.round(mins || 0));
  if (n < 60) return `${n} min late`;
  const h = Math.floor(n / 60), r = n % 60;
  if (h < 24) return r ? `${h}h ${r}m late` : `${h}h late`;
  const d = Math.floor(h / 24), hr = h % 24;
  return hr ? `${d}d ${hr}h late` : `${d}d late`;
}

export default function TodayView() {
  const { data, dataUpdatedAt, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["dashboard-today"],
    queryFn: async () => (await api.get("/dashboard/today")).data,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  });
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => clearInterval(t);
  }, []);

  if (isLoading) return <div className="grid gap-4">{[...Array(3)].map((_, i) => <div key={i} className="h-40 rounded-lg bg-card border border-border animate-pulse" />)}</div>;
  if (!data) return <div className="text-destructive">Unable to load Today.</div>;

  const hasNeeds = Array.isArray(data.needs_you_now);
  const nothing = !hasNeeds && !data.schedule && !data.fleet && !data.next_7_days && !data.my_month;

  return (
    <div className="space-y-6" data-testid="today-view">
      <div className="flex items-center justify-between gap-3">
        <span className="label-mono" data-testid="today-updated">Updated {sinceLabel(dataUpdatedAt)}</span>
        <button onClick={() => refetch()} data-testid="today-refresh"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors min-h-[44px] px-2">
          <ArrowClockwise size={14} className={isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {nothing && (
        <Panel label="Dashboard" title="Welcome" icon={Lock} testid="today-nothing">
          <p className="text-sm text-muted-foreground py-4">Nothing to show here yet. Ask an admin for access to the areas you work in.</p>
        </Panel>
      )}

      {hasNeeds && <NeedsPanel needs={data.needs_you_now} />}
      {data.schedule && <SchedulePanel schedule={data.schedule} />}

      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))" }}>
        {data.fleet && <FleetPanel fleet={data.fleet} wide={!data.my_month} />}
        {data.my_month && <MyMonthPanel my={data.my_month} />}
      </div>

      {data.next_7_days && <Next7Panel next={data.next_7_days} />}
    </div>
  );
}

function NeedsPanel({ needs }) {
  const shown = (needs || []).filter((i) => (i.count || 0) > 0);
  return (
    <Panel label="Attention" title="Needs you now" icon={Warning} testid="today-needs" flush>
      {shown.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground" data-testid="needs-empty">Nothing waiting on you.</p>
      ) : (
        <ul className="divide-y divide-border/60">{shown.map((i) => <NeedsRow key={i.key} item={i} />)}</ul>
      )}
    </Panel>
  );
}

function NeedsRow({ item }) {
  const [open, setOpen] = useState(false);
  const expandable = Array.isArray(item.bookings) && item.bookings.length > 0;
  if (!expandable) {
    return (
      <li>
        <Link to={item.link} data-testid={`needs-${item.key}`} className="px-5 py-3.5 min-h-[44px] flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
          <span className="flex-1 text-sm">{item.label}</span>
          <span className={`font-heading text-lg tnum ${TONE[item.tone] || ""}`}>{fmt(item.count)}</span>
          <CaretRight size={14} className="text-muted-foreground" />
        </Link>
      </li>
    );
  }
  return (
    <li>
      <button onClick={() => setOpen((o) => !o)} data-testid={`needs-${item.key}`} aria-expanded={open}
        className="w-full px-5 py-3.5 min-h-[44px] flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left">
        {open ? <CaretDown size={14} className="text-muted-foreground" /> : <CaretRight size={14} className="text-muted-foreground" />}
        <span className="flex-1 text-sm">{item.label}</span>
        <span className={`font-heading text-lg tnum ${TONE[item.tone] || ""}`}>{fmt(item.count)}</span>
      </button>
      {open && (
        <ul className="bg-white/[0.02] border-t border-border/60 divide-y divide-border/40" data-testid={`needs-${item.key}-list`}>
          {item.bookings.map((b) => (
            <li key={b.id}>
              <Link to={`/bookings/view/${b.id}`} className="pl-11 pr-5 py-3 min-h-[44px] flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
                <span className="flex-1 min-w-0">
                  <span className="font-heading text-sm truncate block">{b.customer_name}</span>
                  <span className="text-xs text-muted-foreground truncate block">{b.vehicle_label || "—"}</span>
                </span>
                {b.late_minutes != null && <span className="text-xs text-red-400 shrink-0">{lateText(b.late_minutes)}</span>}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const KIND = {
  out: { label: "Going out", icon: Clock, tone: "text-sky-400" },
  back: { label: "Due back", icon: ArrowUUpLeft, tone: "text-amber-400" },
  chauffeur: { label: "Chauffeur / tour", icon: IdentificationBadge, tone: "text-primary" },
  internal: { label: "Internal hold", icon: Lock, tone: "text-muted-foreground" },
};

function EntryRow({ e }) {
  const k = KIND[e.kind] || KIND.out;
  const Icon = k.icon;
  return (
    <li>
      <Link to={`/bookings/view/${e.id}`} data-testid={`schedule-${e.id}`} className="px-5 py-3 min-h-[44px] flex items-center gap-3 hover:bg-white/[0.03] transition-colors">
        <span className="text-sm tnum text-muted-foreground shrink-0 w-14">{e.time ? dt(e.time, { hour: "numeric", minute: "2-digit" }) : "—"}</span>
        <Icon size={16} className={`${k.tone} shrink-0`} />
        <span className="flex-1 min-w-0">
          <span className="font-heading text-base truncate block">{e.customer_name}</span>
          <span className="text-xs text-muted-foreground truncate block">{k.label} · {e.vehicle_label || "—"}{e.rep ? ` · ${e.rep}` : ""}</span>
        </span>
        {e.mine && <span className="text-[10px] uppercase tracking-wider text-primary border border-primary/40 rounded px-1.5 py-0.5 shrink-0">Mine</span>}
        {e.tag ? <span className={`text-[10px] uppercase tracking-wider border rounded px-1.5 py-0.5 shrink-0 ${TAG_TONE[e.tag] || "text-muted-foreground border-border"}`}>{e.tag}</span> : null}
      </Link>
    </li>
  );
}

function SchedulePanel({ schedule }) {
  const canSwitch = schedule.everyone_allowed;
  const [view, setView] = useState(canSwitch ? "everyone" : "mine");
  const [limit, setLimit] = useState(15);
  const all = schedule.entries || [];
  const shown = canSwitch && view === "mine" ? all.filter((e) => e.mine) : all;
  const page = shown.slice(0, limit);

  return (
    <Panel label="Today" title="Schedule" icon={CalendarBlank} testid="today-schedule" flush>
      {canSwitch && (
        <div className="px-5 py-3 flex items-center gap-2 border-b border-border/60" role="group" aria-label="Schedule scope">
          <Toggle pressed={view === "mine"} onClick={() => { setView("mine"); setLimit(15); }} testid="schedule-toggle-mine">Mine ({schedule.mine_count})</Toggle>
          <Toggle pressed={view === "everyone"} onClick={() => { setView("everyone"); setLimit(15); }} testid="schedule-toggle-everyone">Everyone ({all.length})</Toggle>
        </div>
      )}
      {shown.length === 0 ? (
        <EmptyState icon={CalendarBlank} title={view === "mine" && canSwitch ? "Nothing of yours today" : "Nothing scheduled today"} hint="Departures, returns and driver jobs for today land here." />
      ) : (
        <>
          <ul className="divide-y divide-border/60">{page.map((e) => <EntryRow key={`${e.kind}-${e.id}`} e={e} />)}</ul>
          {shown.length > limit && (
            <div className="px-5 py-3 flex items-center gap-3 border-t border-border/60">
              <button onClick={() => setLimit((l) => l + 15)} data-testid="schedule-show-more" className="text-xs text-primary hover:underline min-h-[44px]">Show next {Math.min(15, shown.length - limit)}</button>
              <button onClick={() => setLimit(shown.length)} data-testid="schedule-show-all" className="text-xs text-muted-foreground hover:text-foreground min-h-[44px]">Show all {shown.length}</button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}

function FleetPanel({ fleet, wide }) {
  if (fleet.mode === "available_only") {
    return (
      <Panel label="Fleet" title="Right now" icon={Car} testid="today-fleet">
        <Link to="/calendar" data-testid="fleet-available-now" className="block hover:opacity-80 transition-opacity">
          <div className="font-heading text-5xl tnum text-emerald-400">{fmt(fleet.available)}</div>
          <div className="text-xs text-muted-foreground mt-1.5">available now →</div>
        </Link>
      </Panel>
    );
  }
  const noReturn = fleet.no_return_count || 0;
  return (
    <Panel label="Fleet" title="Right now" icon={Car} testid="today-fleet">
      <div className={`grid gap-3 ${wide ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2"}`}>
        <FleetCount label="Out" value={fleet.out} to="/fleet" tone="text-sky-400" icon={Car} />
        <FleetCount label="Available" value={fleet.available} to="/fleet" tone="text-emerald-400" icon={Car} />
        <FleetCount label="In maintenance" value={fleet.maintenance} to="/fleet" tone="text-amber-400" icon={Wrench} />
        <FleetCount label="Late" value={fleet.late} to="/fleet" tone="text-red-400" icon={Clock} />
      </div>
      <div className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground" data-testid="today-fleet-line">
        {fleet.utilization}% of the fleet working{noReturn > 0 ? ` · ${noReturn} ${noReturn === 1 ? "booking" : "bookings"} with no return date` : ""}
      </div>
    </Panel>
  );
}

function FleetCount({ label, value, to, tone, icon: Icon }) {
  return (
    <Link to={to} className="rounded-xl border border-border/60 p-3 hover:bg-white/[0.03] transition-colors min-h-[44px]">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Icon size={13} /> {label}</div>
      <div className={`font-heading text-2xl tnum mt-1 ${tone}`}>{fmt(value)}</div>
    </Link>
  );
}

function MyMonthPanel({ my }) {
  return (
    <Panel label="You" title="My month" icon={Trophy} testid="today-my-month">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Fig label="Cars out" value={fmt(my.cars_out)} accent />
        <Fig label="Points" value={fmt(my.points)} />
        <Fig label="Leads replied" value={fmt(my.leads_replied)} />
        <Fig label="Lead close" value={my.close && my.close.of ? pct1(my.close.rate) : "—"} />
      </div>
      <Link to="/sales-performance" className="mt-4 inline-block text-xs text-primary hover:underline" data-testid="today-my-month-link">See Sales Performance →</Link>
    </Panel>
  );
}

function Fig({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-border/60 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-heading text-2xl tnum mt-1 ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function Next7Panel({ next }) {
  const [openDay, setOpenDay] = useState(null);
  const days = next.days || [];
  return (
    <Panel label="Pipeline" title="Next 7 days" icon={CalendarBlank} testid="today-next7" flush>
      <ul className="divide-y divide-border/60">
        {days.map((d) => {
          const total = d.out_count + d.back_count;
          const isOpen = openDay === d.date;
          return (
            <li key={d.date}>
              <button onClick={() => setOpenDay(isOpen ? null : d.date)} data-testid={`next7-day-${d.date}`} aria-expanded={isOpen}
                className="w-full px-5 py-3.5 min-h-[44px] flex items-center gap-3 hover:bg-white/[0.03] transition-colors text-left">
                {total > 0 ? (isOpen ? <CaretDown size={14} className="text-muted-foreground" /> : <CaretRight size={14} className="text-muted-foreground" />) : <span className="w-3.5" />}
                <span className="flex-1 text-sm">{dt(d.date + "T12:00", { weekday: "short", month: "short", day: "numeric" })}</span>
                <span className="text-xs text-muted-foreground">{d.out_count} out · {d.back_count} back</span>
              </button>
              {isOpen && total > 0 && (
                <ul className="bg-white/[0.02] border-t border-border/60 divide-y divide-border/40" data-testid={`next7-day-${d.date}-list`}>
                  {d.entries.map((e) => <EntryRow key={`${e.kind}-${e.id}`} e={e} />)}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

function Toggle({ pressed, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} aria-pressed={pressed}
      className={`text-xs px-3 min-h-[44px] rounded-full border transition-colors ${pressed ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
      {children}
    </button>
  );
}

function sinceLabel(ts) {
  if (!ts) return "just now";
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins <= 0) return "just now";
  if (mins === 1) return "1 min ago";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h === 1 ? "1 hour ago" : `${h} hours ago`;
}
